import React, { useState, useEffect, useRef } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

type Option = {
  value: string;
  label: string;
};

type MultiSelectAutocompleteProps = {
  options: Option[];
  value: Option[];
  onChange: (selected: Option[]) => void;
  placeholder?: string;
  className?: string;
  isInvalid?: boolean;
  errorMessage?: string;
  showErrorMessage?: boolean;
  name?: string;
  id?: string;
  closeOnSelect?: boolean; // New prop to control dropdown closure behavior
};

const MultiSelectAutocomplete: React.FC<MultiSelectAutocompleteProps> = ({
  options,
  value = [],
  onChange,
  placeholder = 'Select options',
  className = '',
  isInvalid = false,
  errorMessage = '',
  showErrorMessage = true,
  name,
  id,
  closeOnSelect = false, // Default to false to maintain backward compatibility
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term - ensure case insensitive matching
  const filteredOptions = options.filter(
    (option) => {
      if (!searchTerm) return !value.some(item => item.value === option.value);
      
      // Case insensitive search with normalization
      const optionLabelLower = option.label.toLowerCase().trim();
      const searchTermLower = searchTerm.toLowerCase().trim();
      
      // Check if this option is already selected
      const isAlreadySelected = value.some((item) => item.value === option.value);
      
      // Try different matching strategies:
      // 1. Direct inclusion match
      const directMatch = optionLabelLower.includes(searchTermLower);
      
      // 2. Match ignoring special characters and spaces
      const normalizedOptionLabel = optionLabelLower.replace(/[\s\-_]/g, '');
      const normalizedSearchTerm = searchTermLower.replace(/[\s\-_]/g, '');
      const normalizedMatch = normalizedOptionLabel.includes(normalizedSearchTerm);
      
      // 3. Special matching for letters vs numbers (O vs 0, I vs 1, etc.)
      const specialOptionLabel = normalizedOptionLabel
        .replace(/0/g, 'o')
        .replace(/1/g, 'i')
        .replace(/2/g, 'z');
      const specialSearchTerm = normalizedSearchTerm
        .replace(/0/g, 'o')
        .replace(/1/g, 'i')
        .replace(/2/g, 'z');
      const specialMatch = specialOptionLabel.includes(specialSearchTerm);
      
      // Only include options that match at least one strategy and are not already selected
      return (directMatch || normalizedMatch || specialMatch) && !isAlreadySelected;
    }
  );

  // Open dropdown when typing
  useEffect(() => {
    if (searchTerm.length > 0) {
      setIsOpen(true);
    }
  }, [searchTerm]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle selecting an option
  const handleSelectOption = (option: Option) => {
    const newValue = [...value, option];
    onChange(newValue);
    setSearchTerm('');    
    if (closeOnSelect) {
      setIsOpen(false); // Close the dropdown if closeOnSelect is true
    } else {
      inputRef.current?.focus(); // Otherwise, keep the original behavior
    }
  };

  // Handle removing a selected option
  const handleRemoveOption = (optionToRemove: Option) => {
    const newValue = value.filter(
      (option) => option.value !== optionToRemove.value
    );
    onChange(newValue);
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  // Highlight matching text in option label
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) {
      return text;
    }

    const queryLower = query.toLowerCase().trim();
    const textLower = text.toLowerCase();
    const index = textLower.indexOf(queryLower);
    
    if (index === -1) return text;
    
    return (
      <>
        {text.substring(0, index)}
        <span className="bg-yellow-200 text-black font-medium">
          {text.substring(index, index + queryLower.length)}
        </span>
        {text.substring(index + queryLower.length)}
      </>
    );
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className={`flex min-h-[42px] w-full flex-wrap items-center rounded border ${
          isInvalid
            ? 'border-danger'
            : 'border-stroke dark:border-strokedark'
        } bg-gray px-2 py-1 text-black dark:bg-meta-4 dark:text-white ${className}`}
        onClick={() => inputRef.current?.focus()}
        role="combobox"
        aria-expanded={isOpen}
        aria-owns="autocomplete-listbox"
        aria-haspopup="listbox"
        id={id}
      >
        {value.length > 0 ? (
          <div className="flex flex-wrap gap-2 pr-8 w-full">
            {value.map((option) => (
              <div
                key={option.value}
                className="flex items-center gap-1 rounded bg-primary px-2 py-0.5 text-sm text-white"
              >
                <span>{option.label}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveOption(option);
                  }}
                  className="text-white hover:text-opacity-80"
                  aria-label={`Remove ${option.label}`}
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
            <input
              ref={inputRef}
              type="text"
              className="flex-grow min-w-[80px] bg-transparent focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={value.length === 0 ? placeholder : ''}
              onFocus={() => setIsOpen(true)}
              autoComplete="off"
              aria-autocomplete="list"
              aria-controls="autocomplete-listbox"
            />
          </div>
        ) : (
          <div className="relative w-full flex items-center">
            <input
              ref={inputRef}
              type="text"
              className="w-full bg-transparent py-1 focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={placeholder}
              onFocus={() => setIsOpen(true)}
              autoComplete="off"
              aria-autocomplete="list"
              aria-controls="autocomplete-listbox"
            />
            <button
              type="button"
              onClick={toggleDropdown}
              className="absolute right-0 text-gray-500"
              aria-label={isOpen ? "Close options" : "Show options"}
            >
              <ChevronDownIcon className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
        )}
      </div>
      
      {isInvalid && errorMessage && showErrorMessage && (
        <p className="mt-1 text-xs text-danger">{errorMessage}</p>
      )}
      
      {isOpen && (
        <div 
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-stroke bg-white shadow-md dark:border-strokedark dark:bg-boxdark"
          id="autocomplete-listbox"
        >
          {filteredOptions.length > 0 ? (
            <ul className="py-1" role="listbox">
              {filteredOptions.map((option) => (
                <li
                  key={option.value}
                  className="cursor-pointer px-3 py-2 hover:bg-gray dark:hover:bg-meta-4"
                  onClick={() => handleSelectOption(option)}
                  role="option"
                  aria-selected={value.some((item) => item.value === option.value)}
                >
                  {highlightMatch(option.label, searchTerm)}
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">
              {searchTerm ? 'No matching options' : 'No options available'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectAutocomplete;
